import Hero from "../components/Hero";
import ContactForm from "../components/ContactForm";
import hotelImg from "../assests/images/hotel-bg.jpg";

import {
  Mail,
  Phone,
  MapPin,
  Clock3,
  MessageCircle,
  ShieldCheck,
  ArrowRight,
} from "lucide-react";

import { Helmet } from "react-helmet-async";

export default function ContactUs() {
  return (
    <>
      <Helmet>
        <title>Contact Us | Dubai Marina</title>

        <meta
          name="description"
          content="Contact Dubai Marina for hotel bookings, travel services, tours, visa assistance, car rentals and travel support."
        />

        <link
          rel="canonical"
          href="https://marinadubai.com/contact"
        />

        <meta
          property="og:title"
          content="Contact Us | Dubai Marina"
        />

        <meta
          property="og:description"
          content="Get in touch with Dubai Marina for hotel bookings, travel services and assistance."
        />

        <meta
          property="og:url"
          content="https://marinadubai.com/contact"
        />

        <meta
          property="og:type"
          content="website"
        />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="Contact Us | Dubai Marina"
        />

        <meta
          name="twitter:description"
          content="Contact Dubai Marina for hotel bookings, travel services and travel assistance."
        />

        <meta
          name="robots"
          content="index, follow"
        />
      </Helmet>

      <div className="w-full bg-[#F7F5EF]">

        {/* =====================================================
                    HERO
                ===================================================== */}

        <Hero
          variant="inner"
          title="Contact Us"
          subtitle="We're here to help with your hotel booking and travel needs."
          backgroundImage={hotelImg}
        />


        {/* =====================================================
                    CONTACT INTRO
                ===================================================== */}

        <section className="w-full bg-[#F7F5EF] py-14 sm:py-16 lg:py-20">
          <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 xl:px-16">

            <div className="mx-auto max-w-3xl text-center">

              <div className="inline-flex items-center gap-2 rounded-full bg-[#E6F0EF] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[rgb(11_85_82)]">
                <span className="h-2 w-2 rounded-full bg-[rgb(11_85_82)]" />
                Get In Touch
              </div>

              <h1 className="mt-5 text-3xl font-extrabold leading-tight text-slate-900 sm:text-4xl lg:text-5xl">
                Let's Talk About
                <span className="block text-[rgb(11_85_82)]">
                  Your Next Journey
                </span>
              </h1>

              <p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-slate-500 sm:text-lg">
                Have a question about a hotel, booking or travel
                service? Send us a message and our team will be
                happy to assist you.
              </p>

            </div>

          </div>
        </section>


        {/* =====================================================
                    CONTACT INFORMATION + FORM
                ===================================================== */}

        <section className="w-full bg-[#F7F5EF] pb-16 sm:pb-20 lg:pb-24">
          <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 xl:px-16">

            <div className="grid grid-cols-1 gap-8 lg:grid-cols-[380px_minmax(0,1fr)] xl:gap-10">


              {/* =================================================
                                LEFT CONTACT INFORMATION
                            ================================================= */}

              <div className="relative overflow-hidden rounded-[28px] bg-[#073F3D] p-7 sm:p-8 lg:p-9">

                {/* Decorative circles */}

                <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-white/[0.04]" />

                <div className="absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-[#D4A83F]/[0.06]" />


                <div className="relative">

                  {/* Label */}

                  <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.07] px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-[#D4A83F]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#D4A83F]" />
                    Contact Information
                  </div>


                  <h2 className="!text-white mt-5 text-2xl font-extrabold leading-tight text-white sm:text-3xl">
                    We're Always
                    <span className="block text-[#D4A83F]">
                      Happy To Help
                    </span>
                  </h2>


                  <p className="mt-4 text-sm leading-6 text-white/60">
                    Whether you need help with a hotel
                    booking, travel service or general
                    enquiry, our team is ready to assist.
                  </p>


                  {/* Contact items */}

                  <div className="mt-8 space-y-4">


                    {/* Phone */}

                    <a
                      href="tel:+97144473839"
                      className="group flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.05] p-4 transition-all duration-300 hover:bg-white/[0.09]"
                    >

                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 text-[#D4A83F] transition-all duration-300 group-hover:bg-[#D4A83F] group-hover:text-[#073F3D]">
                        <Phone size={19} />
                      </div>

                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/40">
                          Call Us
                        </p>

                        <p className="mt-1 text-sm font-semibold text-white">
                          +971 4 447 3839
                        </p>

                        <p className="mt-1 text-xs text-white/40">
                          Available for assistance
                        </p>
                      </div>

                    </a>


                    {/* Email */}

                    <a
                      href="mailto:info@southtravels.com"
                      className="group flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.05] p-4 transition-all duration-300 hover:bg-white/[0.09]"
                    >

                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 text-[#D4A83F] transition-all duration-300 group-hover:bg-[#D4A83F] group-hover:text-[#073F3D]">
                        <Mail size={19} />
                      </div>

                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/40">
                          Email Us
                        </p>

                        <p className="mt-1 break-all text-sm font-semibold text-white">
                          info@southtravels.com
                        </p>

                        <p className="mt-1 text-xs text-white/40">
                          Send us your enquiry
                        </p>
                      </div>

                    </a>


                    {/* Location */}

                    <div className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.05] p-4">

                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 text-[#D4A83F]">
                        <MapPin size={19} />
                      </div>

                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/40">
                          Location
                        </p>

                        <p className="mt-1 text-sm font-semibold text-white">
                          Dubai, United Arab Emirates
                        </p>

                        <p className="mt-1 text-xs text-white/40">
                          Office 302, 3rd Flr. HDS Tower Cluster F, Jumeirah Lakes Towers Dubai
                        </p>
                      </div>

                    </div>




                  </div>


                  {/* Bottom trust */}

                  <div className="mt-8 flex items-center gap-3 border-t border-white/10 pt-6">

                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#D4A83F]/10">
                      <ShieldCheck
                        size={18}
                        className="text-[#D4A83F]"
                      />
                    </div>

                    <div>
                      <p className="text-xs font-bold text-white">
                        Your information is secure
                      </p>

                      <p className="mt-0.5 text-[11px] text-white/40">
                        We respect your privacy
                      </p>
                    </div>

                  </div>

                </div>

              </div>


              {/* =================================================
                                CONTACT FORM
                            ================================================= */}

              <div>
                <ContactForm />
              </div>

            </div>

          </div>
        </section>





        {/* =====================================================
                    MAP
                    CTA REMOVED — MAP IS HERE INSTEAD
                ===================================================== */}

        <section className="w-full bg-[#F7F5EF] pb-16 sm:pb-20 lg:pb-24">

          <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 xl:px-16">

            {/* Map heading */}

            <div className="mb-7 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">

              <div>

                <div className="inline-flex items-center gap-2 rounded-full bg-[#E6F0EF] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[rgb(11_85_82)]">

                  <MapPin size={13} />

                  Find Us
                </div>

                <h2 className="mt-4 text-3xl font-extrabold text-slate-900 sm:text-4xl">
                  Our Location
                </h2>

                <p className="mt-2 max-w-xl text-sm leading-6 text-slate-500 sm:text-base">
                  Find us in Dubai and get in touch with our
                  team for your travel requirements.
                </p>

              </div>


              <div className="flex items-center gap-2 text-sm font-semibold text-[rgb(11_85_82)]">

                <MapPin size={17} />

                Dubai, United Arab Emirates

              </div>

            </div>


            {/* =================================================
                            MAP CONTAINER
                        ================================================= */}

            <div className="relative overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_15px_50px_rgba(15,23,42,0.08)]">

              {/* Map */}

              <div className="h-[380px] w-full sm:h-[450px] lg:h-[520px]">
                <iframe
                  title="Dubai Marina Office Location"
                  src="https://www.google.com/maps/embed?pb=!1m16!1m12!1m3!1d3613.775341638421!2d55.143142861862316!3d25.0756025365207!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!2m1!1sOffice%20302%2C%203rd%20Flr.%20HDS%20Tower%20Cluster%20F%2C%20Jumeirah%20Lakes%20Towers%20Dubai!5e0!3m2!1sen!2sae!4v1789480084690!5m2!1sen!2sae"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              </div>


              {/* Floating location card */}

              <div className="absolute bottom-5 left-5 right-5 sm:left-7 sm:right-auto sm:max-w-[340px]">

                <div className="rounded-2xl border border-white/70 bg-white/95 p-4 shadow-[0_10px_35px_rgba(15,23,42,0.15)] backdrop-blur-md">

                  <div className="flex items-start gap-3">

                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#073F3D] text-[#D4A83F]">
                      <MapPin size={19} />
                    </div>

                    <div>

                      <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
                        Dubai
                      </p>

                      <p className="mt-1 text-sm font-bold text-slate-900">
                        United Arab Emirates
                      </p>

                      <p className="mt-1 text-xs text-slate-500">
                        Contact our team for directions
                        and assistance.
                      </p>

                    </div>

                  </div>

                </div>

              </div>

            </div>

          </div>

        </section>

      </div>
    </>
  );
}